const pluralRules = new Intl.PluralRules('uk-UA');
const numberFmt = new Intl.NumberFormat('uk-UA');

export type PluralForms = { one: string; few: string; many: string; other?: string };

/** Українська множина: plural(3, {one: 'задача', few: 'задачі', many: 'задач'}) → «задачі». */
export function plural(n: number, forms: PluralForms): string {
  const rule = pluralRules.select(n);
  if (rule === 'one') return forms.one;
  if (rule === 'few') return forms.few;
  if (rule === 'many') return forms.many;
  return forms.other ?? forms.few;
}

/**
 * Нерозривний пробіл між числом і одиницею («15 год», «53,7 %», «1 500,00 грн», «3 задачі»):
 * у вузькій колонці число не відривається від одиниці. Для JSX-тексту — `&nbsp;`, для рядків — NBSP.
 */
export const NBSP = '\u00a0';

/** Число з одиницею через нерозривний пробіл: withUnit('7,5', 'год') → «7,5 год». */
export function withUnit(value: string | number, unit: string): string {
  return `${typeof value === 'number' ? formatNumber(value) : value}${NBSP}${unit}`;
}

export function countLabel(n: number, forms: PluralForms): string {
  return `${formatNumber(n)}${NBSP}${plural(n, forms)}`;
}

export function formatNumber(n: number, opts?: Intl.NumberFormatOptions): string {
  return opts ? new Intl.NumberFormat('uk-UA', opts).format(n) : numberFmt.format(n);
}

export function formatMoney(n: number, currency = 'UAH'): string {
  return new Intl.NumberFormat('uk-UA', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);
}

/** Хвилини → «2 год 30 хв» (число й одиниця — через нерозривний пробіл). */
export function formatMinutes(min: number | null | undefined): string {
  if (min == null) return '';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (!h) return `${m}${NBSP}хв`;
  if (!m) return `${h}${NBSP}год`;
  return `${h}${NBSP}год ${m}${NBSP}хв`;
}

export const TASK_FORMS: PluralForms = { one: 'задача', few: 'задачі', many: 'задач' };
export const DAY_FORMS: PluralForms = { one: 'день', few: 'дні', many: 'днів' };
export const HOUR_FORMS: PluralForms = { one: 'година', few: 'години', many: 'годин' };

/** Ініціали для аватара: «Керівник ЮВ (демо)» → «КЮ». */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const clean = name.replace(/\(.*?\)/g, '').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '');
  return letters.join('') || '?';
}

/** Нормалізація для пошуку: регістр, апострофи, ґ/г, пробіли. */
export function normalizeSearch(s: string): string {
  return s
    .toLocaleLowerCase('uk-UA')
    .replace(/[’ʼ'`]/g, '')
    .replace(/ґ/g, 'г')
    .replace(/\s+/g, ' ')
    .trim();
}

export function matchesSearch(haystack: string, query: string): boolean {
  const q = normalizeSearch(query);
  if (!q) return true;
  return normalizeSearch(haystack).includes(q);
}
